:root {
  --primary: #2563eb;
  --primary-hover: #1d4ed8;
  --bg: #ffffff;
  --surface: #ffffff;
  --text-main: #1f2937;
  --text-muted: #6b7280;
  --border: #e5e7eb;
  --success: #059669;
  --danger: #dc2626;
  --warning: #d97706;
  --radius: 4px;
}

body {
  margin: 0;
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  background-color: var(--bg);
  color: var(--text-main);
  line-height: 1.5;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-feature-settings: 'cv02', 'cv03', 'cv04', 'cv11';
}

button, input, select, textarea, optgroup, .ant-modal, .ant-btn, .ant-input, .ant-select, .ant-checkbox {
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif !important;
}

/* Buttons specifically get a bolder weight than body text for visual
   hierarchy — most MUI/AntD buttons across the app set fontWeight: 400
   inline, so this needs !important to actually win. */
button,
.MuiButtonBase-root,
.MuiButton-root,
.ant-btn {
  font-weight: 600 !important;
}

code, pre, .font-mono {
  font-family: 'Geist Mono', monospace;
}

/* Hide Scrollbars while keeping functionality */
* {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}

*::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
}

/* Typography */
h1 { font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem; }
h2 { font-size: 1.25rem; font-weight: 600; margin-bottom: 1rem; }

/* ==========================================================================
   SWEETALERT2 Z-INDEX & MODAL OVERLAY ELEVATION & INPUT FOCUS FIX
   Ensure Swal popups are ALWAYS on top of all Material UI Dialogs & fully interactive
   ========================================================================== */
.swal2-container {
  z-index: 9999999 !important;
  pointer-events: auto !important;
}

.swal2-popup {
  z-index: 10000000 !important;
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  pointer-events: auto !important;
  /* Liquid Glass — mirrors the CRM repo's own .swal2-popup treatment
     (src/index.css there) exactly, so both apps' alerts feel like the same
     product. A frosted, translucent card over the blurred backdrop below,
     not a flat opaque white box. */
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(30px) saturate(190%) !important;
  -webkit-backdrop-filter: blur(30px) saturate(190%) !important;
  border-radius: 22px !important;
  border: 1px solid rgba(255, 255, 255, 0.85) !important;
  box-shadow:
    0 28px 64px -12px rgba(0, 0, 0, 0.22),
    0 0 0 1px rgba(0, 0, 0, 0.05),
    inset 0 1px 1px rgba(255, 255, 255, 0.95) !important;
}

.swal2-popup.swal2-show {
  animation: hd-swal-spring 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.14) !important;
}

.swal2-popup.swal2-hide {
  animation: hd-swal-close 0.16s cubic-bezier(0.25, 0.1, 0.25, 1) !important;
}

@keyframes hd-swal-spring {
  0% { transform: scale(0.88) translateY(10px); opacity: 0; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
}

@keyframes hd-swal-close {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.92) translateY(8px); opacity: 0; }
}

.swal2-input,
.swal2-select,
.swal2-textarea,
.swal2-radio,
.swal2-checkbox {
  border-radius: 4px !important;
  pointer-events: auto !important;
  user-select: text !important;
  position: relative !important;
  z-index: 10000001 !important;
}

.swal2-backdrop-show {
  z-index: 9999998 !important;
  /* The actual "blurry liquid" effect — blurs whatever's behind the modal
     (the app UI) rather than just dimming it flat. Was previously missing
     entirely; this rule only ever set z-index. Values match the CRM repo's
     own backdrop exactly. */
  backdrop-filter: blur(16px) !important;
  -webkit-backdrop-filter: blur(16px) !important;
  background: rgba(0, 0, 0, 0.26) !important;
  transition: opacity 0.2s ease-in-out !important;
}

.swal2-actions button {
  border-radius: 4px !important;
}

/* ==========================================================================
   BUTTONS - BASE & ZERO COLOR CHANGE ON HOVER (LESS ROUNDED CORNERS)
   Buttons maintain their exact original color & white text on hover
   ========================================================================== */
button,
.ant-btn,
.ant-btn *,
.MuiButton-root,
.MuiButton-root *,
.btn,
.btn-primary,
.btn-secondary,
.btn-access,
.btn-license,
.btn-demo,
.btn-delete,
.btn-tags,
.btn-features,
.btn-whatsapp,
.btn-info,
.plan-billing-btn,
.fb-logout-full-btn,
.antd-platform-dropdown-btn,
.swal2-actions button,
input[type="button"],
input[type="submit"],
input[type="reset"] {
  font-weight: 400 !important;
}

button {
  cursor: pointer;
  border: 1px solid var(--border);
  background: var(--surface);
  padding: 0.5rem 1rem;
  border-radius: 4px !important;
  font-size: 0.875rem;
  font-weight: 400 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--text-main);
  box-sizing: border-box;
  transition: opacity 0.1s ease;
}

/* Neutral button hover - No color change */
button:hover {
  opacity: 0.92;
}

/* Primary Blue Buttons */
button.primary,
.btn-primary {
  background: #2563eb !important;
  color: #ffffff !important;
  border: 1px solid #2563eb !important;
}

button.primary:hover,
.btn-primary:hover {
  background: #2563eb !important;
  border-color: #2563eb !important;
  color: #ffffff !important;
  opacity: 0.92;
}

/* Secondary Slate Buttons */
button.secondary,
.btn-secondary {
  background: #f1f5f9 !important;
  border: 1px solid #cbd5e1 !important;
  color: #334155 !important;
}

button.secondary:hover,
.btn-secondary:hover {
  background: #f1f5f9 !important;
  border-color: #cbd5e1 !important;
  color: #334155 !important;
  opacity: 0.92;
}

/* Access Buttons (Blue) */
button.access,
.btn-access {
  background: #2563eb !important;
  color: #ffffff !important;
  border: 1px solid #2563eb !important;
}

button.access:hover,
.btn-access:hover {
  background: #2563eb !important;
  border-color: #2563eb !important;
  color: #ffffff !important;
  opacity: 0.92;
}

/* ==========================================================================
   Material UI Elevated Action Buttons (Contained Style with Elevation)
   ========================================================================== */
.btn-license,
.btn-demo,
.btn-delete,
.btn-tags,
.btn-features,
.btn-whatsapp,
.btn-access,
.btn-info {
  border-radius: 4px !important;
  font-size: 0.8125rem !important;
  padding: 5px 13px !important;
  line-height: 1.4 !important;
}

button.license,
.btn-license {
  background: #10b981 !important;
  color: #ffffff !important;
  border: none !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  letter-spacing: 0.02em;
  box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12) !important;
  transition: box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) !important;
}
button.license:hover,
.btn-license:hover {
  background: #059669 !important;
  box-shadow: 0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12) !important;
  color: #ffffff !important;
}
button.license:active,
.btn-license:active {
  box-shadow: 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12) !important;
}

button.demo,
.btn-demo {
  background: #f59e0b !important;
  color: #ffffff !important;
  border: none !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  letter-spacing: 0.02em;
  box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12) !important;
  transition: box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) !important;
}
button.demo:hover,
.btn-demo:hover {
  background: #d97706 !important;
  box-shadow: 0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12) !important;
  color: #ffffff !important;
}
button.demo:active,
.btn-demo:active {
  box-shadow: 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12) !important;
}

button.delete,
.btn-delete {
  background: #ef4444 !important;
  color: #ffffff !important;
  border: none !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  letter-spacing: 0.02em;
  box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12) !important;
  transition: box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) !important;
}
button.delete:hover,
.btn-delete:hover {
  background: #dc2626 !important;
  box-shadow: 0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12) !important;
  color: #ffffff !important;
}
button.delete:active,
.btn-delete:active {
  box-shadow: 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12) !important;
}

.btn-tags {
  background: #7c3aed !important;
  color: #ffffff !important;
  border: none !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  letter-spacing: 0.02em;
  box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12) !important;
  transition: box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.btn-tags:hover {
  background: #6d28d9 !important;
  box-shadow: 0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12) !important;
  color: #ffffff !important;
}
.btn-tags:active {
  box-shadow: 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12) !important;
}

.btn-features {
  background: #2563eb !important;
  color: #ffffff !important;
  border: none !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  letter-spacing: 0.02em;
  box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12) !important;
  transition: box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.btn-features:hover {
  background: #1d4ed8 !important;
  box-shadow: 0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12) !important;
  color: #ffffff !important;
}
.btn-features:active {
  box-shadow: 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12) !important;
}

.btn-whatsapp {
  background: #16a34a !important;
  color: #ffffff !important;
  border: none !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  letter-spacing: 0.02em;
  box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12) !important;
  transition: box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.btn-whatsapp:hover {
  background: #15803d !important;
  box-shadow: 0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12) !important;
  color: #ffffff !important;
}
.btn-whatsapp:active {
  box-shadow: 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12) !important;
}

.btn-access {
  background: #0284c7 !important;
  color: #ffffff !important;
  border: none !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  letter-spacing: 0.02em;
  box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12) !important;
  transition: box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.btn-access:hover {
  background: #0369a1 !important;
  box-shadow: 0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12) !important;
  color: #ffffff !important;
}
.btn-access:active {
  box-shadow: 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12) !important;
}

.btn-info {
  background: #64748b !important;
  color: #ffffff !important;
  border: none !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  letter-spacing: 0.02em;
  box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12) !important;
  transition: box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.btn-info:hover {
  background: #475569 !important;
  box-shadow: 0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12) !important;
  color: #ffffff !important;
}
.btn-info:active {
  box-shadow: 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12) !important;
}

/* ==========================================================================
   Ant Design & Material UI Button Hover Protections (Zero Color Shift)
   ========================================================================== */
.ant-btn {
  font-family: inherit !important;
  font-weight: 400 !important;
  border-radius: 4px !important;
}

.ant-input, .ant-select-selector {
  border-radius: 4px !important;
}

.ant-btn-primary {
  background: #2563eb !important;
  color: #ffffff !important;
  border-color: #2563eb !important;
}

.ant-btn-primary:not(:disabled):hover {
  background: #2563eb !important;
  border-color: #2563eb !important;
  color: #ffffff !important;
  opacity: 0.92;
}

.ant-btn-default:not(:disabled):hover {
  background: #ffffff !important;
  border-color: #d9d9d9 !important;
  color: rgba(0, 0, 0, 0.88) !important;
  opacity: 0.92;
}

.ant-btn-dangerous.ant-btn-primary {
  background: #ef4444 !important;
  border-color: #ef4444 !important;
  color: #ffffff !important;
}

.ant-btn-dangerous.ant-btn-primary:not(:disabled):hover {
  background: #ef4444 !important;
  border-color: #ef4444 !important;
  color: #ffffff !important;
  opacity: 0.92;
}

.ant-btn-dangerous:not(.ant-btn-primary) {
  border-color: #fca5a5 !important;
  color: #dc2626 !important;
  background: #ffffff !important;
}

.ant-btn-dangerous:not(.ant-btn-primary):not(:disabled):hover {
  background: #ffffff !important;
  border-color: #fca5a5 !important;
  color: #dc2626 !important;
  opacity: 0.92;
}

/* Material UI Contained & Outlined Buttons Hover (Zero Color Shift) */
.MuiButton-root {
  font-family: inherit !important;
  border-radius: 4px !important;
}

.MuiPaper-root, .MuiDialog-paper {
  border-radius: 4px !important;
}

.MuiButton-containedPrimary {
  background-color: #2563eb !important;
  color: #ffffff !important;
}

.MuiButton-containedPrimary:hover {
  background-color: #2563eb !important;
  color: #ffffff !important;
  opacity: 0.92;
}

.MuiButton-containedError {
  background-color: #ef4444 !important;
  color: #ffffff !important;
}

.MuiButton-containedError:hover {
  background-color: #ef4444 !important;
  color: #ffffff !important;
  opacity: 0.92;
}

.MuiButton-containedSuccess {
  background-color: #10b981 !important;
  color: #ffffff !important;
}

.MuiButton-containedSuccess:hover {
  background-color: #10b981 !important;
  color: #ffffff !important;
  opacity: 0.92;
}

.MuiButton-outlined:hover {
  background-color: transparent !important;
  opacity: 0.92;
}

.MuiButton-text:hover {
  background-color: transparent !important;
  opacity: 0.92;
}

/* Inputs */
input, select {
  width: 100%;
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  font-size: 0.8125rem;
  background: white;
  color: var(--text-main);
  box-sizing: border-box;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 1em;
  padding-right: 2rem;
}

input:focus, select:focus {
  outline: none;
  border-color: var(--primary);
}

/* Cards & Containers */
.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);
}

.search-bar {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  margin-bottom: 1.5rem;
  background: #f9fafb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* Table */
.table-wrapper {
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th {
  text-align: left;
  padding: 0.75rem 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  text-transform: none;
  color: #475569;
  background: #ffffff !important;
  border-bottom: 1px solid var(--border);
}

.table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);
  font-size: 0.875rem;
}

.table tr:last-child td {
  border-bottom: none;
}

/* Badges */
.badge {
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: none;
  border: 1px solid transparent;
}

.badge-demo { background: #fef3c7; color: #92400e; border-color: #fde68a; }
.badge-live { background: #dcfce7; color: #166534; border-color: #bbf7d0; }
.badge-expired { background: #fee2e2; color: #991b1b; border-color: #fecaca; }

/* Ticket Status & Priority */
.badge-open { background: #eff6ff; color: #2563eb; border-color: #dbeafe; }
.badge-accepted { background: #f0fdf4; color: #16a34a; border-color: #dcfce7; }
.badge-pending { background: #fffbeb; color: #d97706; border-color: #fef3c7; }
.badge-completed { background: #f9fafb; color: #4b5563; border-color: #e5e7eb; }

.badge-low { background: #f3f4f6; color: #374151; }
.badge-medium { background: #dbeafe; color: #1e40af; }
.badge-high { background: #ffedd5; color: #9a3412; }
.badge-urgent { background: #fee2e2; color: #991b1b; }

/* Chat History Layout */
.chat-history::-webkit-scrollbar {
  width: 6px;
}
.chat-history::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}
.chat-history::-webkit-scrollbar-track {
  background: transparent;
}

/* Login Form Specific */
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
}

.mobile-barrier {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  padding: 2rem;
  text-align: center;
}

.mobile-barrier-card {
  background: white;
  padding: 3rem 2rem;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  width: 100%;
}

.mobile-barrier-card h2 {
  font-size: 1.5rem;
  color: #0f172a;
  margin-bottom: 0.75rem;
}

.mobile-barrier-card p {
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
}

.login-card {
  width: 100%;
  max-width: 360px;
  background: white;
  padding: 2rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

/* Sidebar Layout (Facebook Business Suite Style) */
.app-layout {
  display: flex;
  min-height: 100vh;
  background-color: #f0f2f5;
}

.sidebar {
  width: 240px;
  background: #ffffff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: sticky;
  top: 0;
  flex-shrink: 0;
  box-sizing: border-box;
}

/* Sidebar Brand Header (Clean, Minimal & Aligned with Top Header) */
.fb-sidebar-header {
  height: 64px;
  min-height: 64px;
  max-height: 64px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  user-select: none;
  background: #ffffff;
  box-sizing: border-box;
  transition: background-color 0.15s ease;
}

.fb-sidebar-header:hover {
  background-color: #f8fafc;
}

.fb-business-title {
  font-size: 13.5px;
  font-weight: 500;
  color: #0f172a;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

/* Ant Design Platform Dropdown Container & Button */
.fb-dropdown-container {
  padding: 12px 12px 6px 12px;
  background: #ffffff;
}

.antd-platform-dropdown-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 10px;
  font-size: 12.5px;
  font-weight: 400 !important;
  color: #0f172a;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  box-sizing: border-box;
}

.antd-platform-dropdown-btn:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
}

.antd-dropdown-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  font-weight: 500;
  font-size: 12.5px;
  color: #0f172a;
}

/* Navigation List */
.sidebar-nav {
  padding: 8px 10px 16px 10px;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  gap: 3px;
}

.fb-nav-section-title {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  padding: 12px 10px 4px 10px;
  letter-spacing: 0.02em;
  text-transform: none;
  user-select: none;
}

.fb-nav-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 400 !important;
  color: #475569;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  width: 100%;
  text-align: left;
  box-sizing: border-box;
  transition: all 0.15s cubic-bezier(0.16, 1, 0.3, 1);
}

.fb-nav-icon-wrap {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.fb-nav-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.01em;
}

.fb-nav-badge {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  font-size: 11px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 4px;
  line-height: 1.4;
}

.fb-nav-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 8px 8px;
}

/* Hover Effect: Soft, refined slate tint with subtle icon accent */
.fb-nav-link:hover {
  background: #f8fafc;
  border-color: #f1f5f9;
  color: #0f172a;
}

.fb-nav-link:hover .fb-nav-icon-wrap {
  color: #2563eb;
  transform: scale(1.05);
}

/* Active / Selected State: Crisp left vertical accent indicator bar, hairline border, rich typography */
.fb-nav-link.active {
  background: #f8fafc;
  border-color: #e2e8f0;
  color: #0f172a;
  font-weight: 500 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.fb-nav-link.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 6px;
  bottom: 6px;
  width: 3px;
  background: #2563eb;
  border-radius: 0 2px 2px 0;
}

.fb-nav-link.active .fb-nav-icon-wrap {
  color: #2563eb;
}

.fb-nav-link.active:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.fb-nav-link-return {
  color: #64748b;
  border: 1px dashed #e2e8f0;
}

.fb-nav-link-return:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #0f172a;
}

/* Sidebar Footer */
.fb-sidebar-footer {
  padding: 10px 10px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fb-logout-full-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  font-size: 12.5px;
  font-weight: 400 !important;
  color: #64748b;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.12s ease;
}

.fb-logout-full-btn:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

.main-content {
  flex: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

.content-header {
  background: #ffffff;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.content-header-top {
  height: 64px;
  min-height: 64px;
  max-height: 64px;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
  box-sizing: border-box;
}

.content-header-title {
  font-size: 1.25rem;
  font-weight: 500;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: -0.02em;
}

.content-body {
  padding: 1.5rem 2rem;
  flex: 1;
  box-sizing: border-box;
}

.search-filter-bar {
  padding: 10px 2rem;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  box-sizing: border-box;
}

.search-input-wrapper {
  position: relative;
  flex: 1;
}

.search-input-wrapper input {
  padding-left: 2.25rem;
}

.search-icon-svg {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.filter-select-wrapper {
  position: relative;
  width: 180px;
}

.filter-select-wrapper select {
  padding-left: 2.25rem;
}

.filter-icon-svg {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.sidebar-nav::-webkit-scrollbar {
  width: 4px;
}
.sidebar-nav::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

/* ==========================================================================
   TEAM MANAGEMENT & RBAC STYLES
   ========================================================================== */
.team-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.team-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.team-role-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
  letter-spacing: 0.01em;
}

.team-role-superadmin {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.team-role-admin {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.team-role-support {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.team-role-developer {
  background: #faf5ff;
  color: #9333ea;
  border: 1px solid #e9d5ff;
}

.team-role-custom {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.perm-pill-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.perm-pill {
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.perm-pill.active {
  background: #e0e7ff;
  color: #3730a3;
  font-weight: 500;
}

.status-badge-active {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1;
  white-space: nowrap;
}

.status-badge-disabled {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1;
  white-space: nowrap;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.permission-checkbox-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.permission-checkbox-card:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.permission-checkbox-card.checked {
  border-color: #2563eb;
  background: #eff6ff;
}

.permission-checkbox-card .ant-checkbox-wrapper {
  margin-top: 2px;
}

.permission-checkbox-title {
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 6px;
}

.permission-checkbox-desc {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 2px;
  line-height: 1.4;
}

.software-chip {
  padding: 2px 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #334155;
}

.team-stat-card {
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.team-stat-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* User Profile Badge in Footer */
.user-profile-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 12px;
  margin-bottom: 8px;
}

.user-profile-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50% !important;
  background: #2563eb;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  overflow: hidden;
  flex-shrink: 0;
}

.user-profile-meta {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-align: left;
}

.user-profile-name {
  font-size: 12px;
  font-weight: 500;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-profile-role {
  font-size: 10px;
  color: #64748b;
  text-transform: none;
}

/* Plan Packages & Pricing Styles */
.plan-packages-container {
  padding: 2rem;
  max-width: 1200px;
}

.plan-billing-switcher {
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 8px;
  gap: 4px;
}

.plan-billing-btn {
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400 !important;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
}

.plan-billing-btn.active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.plan-card-featured {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  position: relative;
  display: flex;
  flex-direction: column;
}

.plan-card-standard {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  position: relative;
  display: flex;
  flex-direction: column;
  transition: border-color 0.15s ease;
}

.plan-card-standard:hover {
  border-color: #cbd5e1;
}

.plan-limit-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 9px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.feature-toggle-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.feature-toggle-item:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.feature-toggle-item.active {
  border-color: #93c5fd;
  background: #f0f7ff;
}

.feature-category-header {
  font-size: 12px;
  font-weight: 500;
  text-transform: none;
  color: #64748b;
  margin: 1.25rem 0 0.5rem;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Universal Table Headers & Title Bars: Pure White, No All-Caps, No Bold */
.table th,
.ant-table-thead > tr > th,
.ant-table-header th,
.ant-table-thead th {
  background: #ffffff !important;
  font-weight: 500 !important;
  text-transform: none !important;
  color: #475569 !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.table-wrapper {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* Ant Design Switch - Smoothly Pill-Rounded */
.ant-switch {
  border-radius: 100px !important;
}

.ant-switch.ant-switch-small {
  border-radius: 100px !important;
}

.ant-switch .ant-switch-handle,
.ant-switch .ant-switch-handle::before {
  border-radius: 50% !important;
}

.ant-switch.ant-switch-checked {
  background: #2563eb !important;
}

.ant-switch.ant-switch-checked:hover:not(.ant-switch-disabled) {
  background: #1d4ed8 !important;
}

/* ==========================================================================
   MODERN HIGH-END PLANS & PRICING UI (MATCHING LANDING PAGE DETAIL)
   ========================================================================== */

/* 3D Tactile Range Slider Track & Thumb */
input[type="range"].seat-slider-3d {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 12px;
  border-radius: 9999px;
  background: #dbe2ea;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(0, 0, 0, 0.12), 0 1px 0 rgba(255, 255, 255, 0.95);
  outline: none;
  cursor: pointer;
  margin: 6px 0;
}

/* Webkit thumb (Chrome, Safari, Edge) */
input[type="range"].seat-slider-3d::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 50%, #2563eb 0%, #1d4ed8 40%, #ffffff 42%, #f8fafc 70%, #cbd5e1 100%);
  border: 2.5px solid #ffffff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25), 0 1.5px 3px rgba(0, 0, 0, 0.15), inset 0 1.5px 1.5px rgba(255, 255, 255, 1), inset 0 -2px 2px rgba(0, 0, 0, 0.15);
  cursor: grab;
  transition: transform 0.1s ease;
}

input[type="range"].seat-slider-3d:active::-webkit-slider-thumb {
  cursor: grabbing;
  transform: scale(1.08);
  box-shadow: 0 5px 12px rgba(37, 99, 235, 0.35), 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Firefox thumb */
input[type="range"].seat-slider-3d::-moz-range-thumb {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 50%, #2563eb 0%, #1d4ed8 40%, #ffffff 42%, #f8fafc 70%, #cbd5e1 100%);
  border: 2.5px solid #ffffff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25), 0 1.5px 3px rgba(0, 0, 0, 0.15), inset 0 1.5px 1.5px rgba(255, 255, 255, 1), inset 0 -2px 2px rgba(0, 0, 0, 0.15);
  cursor: grab;
}

/* Modern Plans Container */
.modern-plans-wrapper {
  max-width: 1240px;
  margin: 0 auto;
  padding: 1rem 0 3rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.modern-plans-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.modern-plans-card-pad {
  padding: 1.75rem 2rem;
}

/* 3D Tactile Cycle Switcher */
.tactile-cycle-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 4px;
  background: #e2e8f0;
  border: 1px solid #cbd5e1;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.12), inset 0 1px 2px rgba(0, 0, 0, 0.08), 0 1px 0 rgba(255, 255, 255, 0.9);
}

.tactile-cycle-btn {
  position: relative;
  z-index: 10;
  padding: 8px 18px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 400 !important;
  border: none !important;
  background: transparent !important;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  user-select: none;
  transition: color 0.15s ease;
}

.tactile-cycle-btn.active {
  color: #ffffff !important;
}

.tactile-cycle-btn.inactive {
  color: #475569 !important;
}

.tactile-cycle-btn.inactive:hover {
  color: #0f172a !important;
}

.tactile-cycle-pill {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  background: linear-gradient(to bottom, #2563eb, #1d4ed8 60%, #1e40af);
  border: 1px solid #1d4ed8;
  box-shadow: 0 4px 10px rgba(30, 64, 175, 0.4), 0 2px 4px rgba(0, 0, 0, 0.15), inset 0 1.5px 1px rgba(255, 255, 255, 0.4), inset 0 -2px 1.5px rgba(0, 0, 0, 0.25);
  transform: translateY(-1px);
}

/* Stepper Pill */
.seat-stepper-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #cbd5e1;
  background: #eef2f6;
  border-radius: 9999px;
  padding: 4px;
  box-shadow: inset 0 1.5px 3px rgba(0, 0, 0, 0.08);
}

.seat-stepper-btn {
  width: 36px;
  height: 36px;
  border-radius: 50% !important;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0 !important;
  transition: all 0.15s ease;
}

.seat-stepper-btn.minus {
  background: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  color: #334155 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08), inset 0 1px 0 #ffffff;
}

.seat-stepper-btn.minus:hover:not(:disabled) {
  background: #f8fafc !important;
}

.seat-stepper-btn.plus {
  background: linear-gradient(to bottom, #2563eb, #1d4ed8 70%, #1e40af) !important;
  border: 1px solid #1e40af !important;
  color: #ffffff !important;
  box-shadow: 0 3px 6px rgba(30, 64, 175, 0.35), inset 0 1.5px 1px rgba(255, 255, 255, 0.4);
}

.seat-stepper-btn.plus:hover {
  filter: brightness(1.06);
}

/* 3D Primary Button */
.btn-primary-3d {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 22px;
  border-radius: 9999px !important;
  font-size: 13.5px;
  font-weight: 400 !important;
  color: #ffffff !important;
  background: linear-gradient(to bottom, #2563eb, #1d4ed8 60%, #1e40af) !important;
  border: 1px solid #1d4ed8 !important;
  box-shadow: 0 4px 10px rgba(30, 64, 175, 0.35), 0 2px 4px rgba(0, 0, 0, 0.15), inset 0 1.5px 1px rgba(255, 255, 255, 0.4), inset 0 -2px 1.5px rgba(0, 0, 0, 0.25) !important;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;
}

.btn-primary-3d:hover {
  filter: brightness(1.05);
}

.btn-primary-3d:active {
  transform: translateY(1px);
  box-shadow: 0 2px 4px rgba(30, 64, 175, 0.3), inset 0 1px 2px rgba(0, 0, 0, 0.3) !important;
}

/* 3D Secondary Button */
.btn-secondary-3d {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 9px 18px;
  border-radius: 9999px !important;
  font-size: 13px;
  font-weight: 400 !important;
  color: #334155 !important;
  background: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05), inset 0 1px 0 #ffffff !important;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;
}

.btn-secondary-3d:hover {
  background: #f8fafc !important;
  color: #0f172a !important;
  border-color: #94a3b8 !important;
}

/* Perforated Order Summary Receipt */
.receipt-box {
  position: relative;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #fafbfc;
  padding: 1.75rem 2rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.receipt-tear-line {
  position: relative;
  margin: 1.5rem -2rem;
}

.receipt-tear-line::before {
  content: '';
  display: block;
  border-top: 2px dashed #cbd5e1;
}

.receipt-cutout-left {
  position: absolute;
  left: -12px;
  top: -12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ffffff;
  border-right: 1px solid #cbd5e1;
  box-shadow: inset -2px 0 3px rgba(0, 0, 0, 0.06);
}

.receipt-cutout-right {
  position: absolute;
  right: -12px;
  top: -12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ffffff;
  border-left: 1px solid #cbd5e1;
  box-shadow: inset 2px 0 3px rgba(0, 0, 0, 0.06);
}

/* Ant Checkbox Mock */
.ant-mock-checkbox {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1.5px solid #d9d9d9;
  background: #ffffff;
  transition: all 0.2s ease;
  flex-shrink: 0;
  cursor: pointer;
}

.ant-mock-checkbox.checked {
  background: #1677ff;
  border-color: #1677ff;
  box-shadow: 0 0 0 3px rgba(22, 119, 255, 0.18);
}

.ant-mock-checkbox.disabled {
  background: #f5f5f5;
  border-color: #d9d9d9;
  cursor: not-allowed;
  opacity: 0.6;
}


